import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Image01, RefreshCw01, X } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"

// In-page camera modal shared by every estimate wizard.
//
// Replaces the OS native camera (which closes after one shot) with a
// `getUserMedia` live preview that stays open across multiple snaps.
// Each snap is grabbed off the video element via canvas and fed to the
// existing capture pipeline (`onCapture`), so AI evaluation runs in
// parallel while the user keeps shooting.
//
// Falls back to the file picker if the camera is denied or the browser
// doesn't support `getUserMedia`.

interface Snap {
  url: string
  takenAt: number
}

interface InPageCameraProps {
  open: boolean
  onClose: () => void
  // Called for every snap. The pipeline upstream (preflight + AI eval +
  // 3-concurrent limiter) is identical to the file-input path.
  onCapture: (file: File) => void
  // Closes the camera and opens the OS file picker — used for the
  // "Library" affordance and the permission-denied fallback.
  onPickFromLibrary: () => void
}

export function InPageCamera({
  open,
  onClose,
  onCapture,
  onPickFromLibrary,
}: InPageCameraProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [facing, setFacing] = useState<"environment" | "user">("environment")
  const [error, setError] = useState<string | null>(null)
  const [snaps, setSnaps] = useState<Snap[]>([])
  const [flash, setFlash] = useState(false)
  const [ready, setReady] = useState(false)

  // (Re-)open the stream when the modal opens or the user flips the
  // camera. The cleanup stops every track so the camera light goes off
  // immediately on close — leaving a stream alive after unmount is the
  // classic getUserMedia footgun.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setReady(false)
    setError(null)

    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("unsupported")
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          await videoRef.current.play().catch(() => {
            // Autoplay can reject silently on some browsers; the
            // <video autoPlay playsInline> fallback usually works.
          })
        }
        setReady(true)
      } catch (err) {
        const denied =
          err instanceof DOMException &&
          (err.name === "NotAllowedError" || err.name === "SecurityError")
        setError(
          denied
            ? "Camera access blocked. Use Library, or enable the camera in your browser settings."
            : "Couldn't open the camera on this device. Use Library instead.",
        )
      }
    }

    start()

    return () => {
      cancelled = true
      const stream = streamRef.current
      if (stream) {
        stream.getTracks().forEach((t) => t.stop())
        streamRef.current = null
      }
    }
  }, [open, facing])

  // Drop the local thumbnails when the modal closes so re-opening the
  // camera starts with a clean strip. Uploaded photos are owned by the
  // wizard's state — these snaps are purely a confidence affordance.
  useEffect(() => {
    if (open) return
    setSnaps((prev) => {
      prev.forEach((s) => URL.revokeObjectURL(s.url))
      return []
    })
    setError(null)
  }, [open])

  useEffect(() => {
    return () => {
      // Final unmount: revoke any remaining preview URLs.
      streamRef.current?.getTracks().forEach((t) => t.stop())
    }
  }, [])

  const handleSnap = () => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || !ready) return

    const w = video.videoWidth
    const h = video.videoHeight
    if (!w || !h) return

    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.drawImage(video, 0, 0, w, h)

    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const ts = Date.now()
        const file = new File([blob], `estimate-camera-${ts}.jpg`, {
          type: "image/jpeg",
          lastModified: ts,
        })
        const previewUrl = URL.createObjectURL(blob)
        setSnaps((prev) => [...prev, { url: previewUrl, takenAt: ts }])
        onCapture(file)

        setFlash(true)
        try {
          navigator.vibrate?.(15)
        } catch {
          // ignore — iOS Safari doesn't expose vibrate.
        }
        setTimeout(() => setFlash(false), 90)
      },
      "image/jpeg",
      0.92,
    )
  }

  const handleSwitch = () => {
    setFacing((f) => (f === "environment" ? "user" : "environment"))
  }

  const handleLibrary = () => {
    onClose()
    // Defer the picker click until after the modal unmounts — clicking
    // the file input while the camera is still alive can cause the OS
    // to interrupt the stream teardown on some Androids.
    setTimeout(onPickFromLibrary, 60)
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="estimate-in-page-camera"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 bg-black"
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="absolute inset-0 w-full h-full object-cover"
          />
          <canvas ref={canvasRef} className="hidden" />

          <AnimatePresence>
            {flash && (
              <motion.div
                key="flash"
                initial={{ opacity: 0.85 }}
                animate={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.16 }}
                className="absolute inset-0 bg-white pointer-events-none"
              />
            )}
          </AnimatePresence>

          <div className="absolute top-0 inset-x-0 px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 flex items-center justify-between bg-gradient-to-b from-black/55 to-transparent">
            <button
              type="button"
              onClick={onClose}
              className="size-10 grid place-items-center rounded-full bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm"
              aria-label="Close camera"
            >
              <X className="size-5" aria-hidden />
            </button>
            <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-white/85">
              {snaps.length === 0
                ? "Capture multiple shots"
                : snaps.length === 1
                  ? "1 shot taken"
                  : `${snaps.length} shots taken`}
            </p>
            <button
              type="button"
              onClick={handleSwitch}
              className="size-10 grid place-items-center rounded-full bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm"
              aria-label="Switch camera"
            >
              <RefreshCw01 className="size-4" aria-hidden />
            </button>
          </div>

          {error && (
            <div className="absolute inset-0 grid place-items-center px-6">
              <div className="max-w-sm w-full rounded-2xl bg-zinc-900/85 backdrop-blur-md text-white p-6 text-center space-y-3">
                <p className="text-[15px] font-semibold leading-snug">
                  {error}
                </p>
                <div className="flex flex-col gap-2 pt-1">
                  <button
                    type="button"
                    onClick={handleLibrary}
                    className="w-full h-12 rounded-xl bg-white text-zinc-900 text-[14px] font-semibold flex items-center justify-center gap-2"
                  >
                    <Image01 className="size-4" aria-hidden />
                    Choose from library
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-full h-10 rounded-xl border border-white/30 text-white text-[13px] font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {!error && snaps.length > 0 && (
            <div className="absolute left-0 right-0 bottom-[152px] sm:bottom-[168px] px-4">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {snaps.slice(-6).map((s) => (
                  <div
                    key={s.takenAt}
                    className="size-14 shrink-0 rounded-lg overflow-hidden ring-2 ring-white/85 shadow-md"
                  >
                    <img
                      src={s.url}
                      alt="Recent shot"
                      className="size-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {!error && (
            <div className="absolute bottom-0 inset-x-0 px-6 pt-4 pb-[max(env(safe-area-inset-bottom),16px)] bg-gradient-to-t from-black/70 to-transparent">
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={handleLibrary}
                  className="size-12 rounded-full bg-black/40 hover:bg-black/60 text-white grid place-items-center backdrop-blur-sm"
                  aria-label="Choose from library"
                >
                  <Image01 className="size-5" aria-hidden />
                </button>

                <motion.button
                  type="button"
                  onClick={handleSnap}
                  disabled={!ready}
                  whileTap={{ scale: 0.92 }}
                  className={cx(
                    "size-20 rounded-full ring-4 ring-white/90 grid place-items-center shadow-2xl transition-opacity duration-150",
                    ready ? "bg-white" : "bg-white/40 cursor-not-allowed",
                  )}
                  aria-label="Take photo"
                >
                  <span className="size-16 rounded-full bg-white border-4 border-zinc-200" />
                </motion.button>

                <button
                  type="button"
                  onClick={onClose}
                  className={cx(
                    "h-12 px-5 rounded-full text-[14px] font-semibold shadow-lg transition-colors duration-150",
                    snaps.length > 0
                      ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                      : "bg-white/15 hover:bg-white/25 text-white border border-white/30",
                  )}
                >
                  Done
                </button>
              </div>
              <p className="mt-3 text-center text-[11px] font-medium text-white/75">
                Photos save automatically. Tap Done when finished.
              </p>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
